import _styled, { default as _styled2, default as _styled3, default as _styled4 } from "styled-components";
import { graphql, useFragment, type FragmentType } from "~/gql/runtime";
import { Grid } from "~/features/ui/grid";
import { LinkBarsItemLink, type LinkBarsItemLinkViewProps } from "~/features/blocks/inventory/link-bars/link-bars-item.link";
import { getThemeStyle, type Mode, type Theme } from "~/features/ui/get-theme-style";
const fragment = graphql(`
  fragment LinkBarsItem on blocks_linkBarsItem_BlockType {
    title: baseText
    links: children {
      ...LinkBarsItemLink
    }
  }
`);
type Props = FragmentType<typeof fragment> & {
  theme: Theme;
  mode: Mode;
  count: string;
};
export type LinkBarsItemViewProps<TLink = Omit<LinkBarsItemLinkViewProps, "mode" | "theme">> = {
  count: string;
  getLinkKey?: (link: TLink, index: number) => React.Key;
  links?: TLink[] | null;
  mode: Mode;
  renderLink: (link: TLink, index: number) => React.ReactNode;
  theme: Theme;
  title?: string | null;
};
export function LinkBarsItemView<TLink = Omit<LinkBarsItemLinkViewProps, "mode" | "theme">>({
  count,
  getLinkKey,
  links,
  mode,
  renderLink,
  theme,
  title
}: LinkBarsItemViewProps<TLink>) {
  const themeStyle = getThemeStyle({
    mode,
    theme
  });
  // v3: white theme renders true white (helper maps "white" to grey-20).
  const surface = theme === "grey" ? themeStyle.background : {
    "backgroundColor": "var(--bg-primary)"
  };
  return (
    // Name + button live together in one white box; borders come from the
    // surrounding frame (left/right) so there is no split between the columns.
    <_StyledGrid $_css={[themeStyle.border, surface]}>
      <_StyledDiv>
        <_StyledDiv2>
          <span>{count}</span>
          <h2>{title}</h2>
        </_StyledDiv2>
      </_StyledDiv>
      <_StyledDiv3>
        {links?.map((link, idx) => <div key={getLinkKey?.(link, idx) || idx}>{renderLink(link, idx)}</div>)}
      </_StyledDiv3>
    </_StyledGrid>
  );
}
export function LinkBarsItem(props: Props) {
  const {
    count,
    mode,
    theme,
    ...rest
  } = props;
  const {
    title,
    links
  } = useFragment(fragment, rest);
  return <LinkBarsItemView count={count} links={links} mode={mode} renderLink={link => <LinkBarsItemLink theme={theme} mode={mode} {...link} />} theme={theme} title={title} />;
}
var _StyledGrid = /*#__PURE__*/_styled(Grid).withConfig({
  componentId: "sc-sf-16vb3p2-0"
})`${p => p.$_css}`;
var _StyledDiv = _styled2("div").withConfig({
  componentId: "sc-sf-16vb3p2-1"
})({
  "gridColumn": "1 / -1",
  "@media (min-width: 1024px)": {
    "gridColumn": "span 4 / span 4"
  }
});
var _StyledDiv2 = _styled3("div").withConfig({
  componentId: "sc-sf-16vb3p2-2"
})({
  "display": "flex",
  "alignItems": "center",
  "columnGap": "1.6rem",
  "paddingLeft": "1.6rem",
  "paddingRight": "1.6rem",
  "paddingTop": "4rem",
  "paddingBottom": "4rem",
  "fontFamily": "Aeonik, ui-serif, Georgia, Cambria, \"Times New Roman\", Times, serif",
  "fontSize": "1.4rem",
  "lineHeight": "1.1",
  "letterSpacing": "0.028000000000000004rem",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "paddingLeft": "4rem",
    "paddingRight": "4rem",
    "fontSize": "1.6rem",
    "lineHeight": "1.1",
    "letterSpacing": "0.032rem",
    "fontWeight": "400"
  }
});
var _StyledDiv3 = /*#__PURE__*/_styled4("div").withConfig({
  displayName: "link-bars-item___StyledDiv3",
  componentId: "sc-d207um-0"
})({
  "gridColumn": "1 / -1",
  "display": "flex",
  "flexDirection": "column",
  "rowGap": "1.6rem",
  "padding": "1.6rem",
  "@media (min-width: 1024px)": {
    "gridColumn": "span 8 / span 8",
    "rowGap": "2.4rem",
    "padding": "4rem"
  }
});